import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserPermissions } from './useUserPermissions';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';

interface Contact {
  id: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  primary_email?: string;
  primary_phone?: string;
  primary_phone_country_code?: string;
  status?: string;
  country_of_birth?: string;
  city?: string;
  created_at?: string;
  deals?: Array<{ id: string; stage: string }>;
  [key: string]: any;
}

interface UseContactsOptions {
  page?: number;
  limit?: number;
  searchQuery?: string;
  salesConsultantFilter?: string[];
  sdrFilter?: string[];
  verificationFilter?: string[];
  enabled?: boolean;
  isKanbanMode?: boolean;
  sourceFilter?: string[];
  channelFilter?: string[];
  contactTypeFilter?: string[];
  createdByFilter?: string[];
  dealStageFilter?: string[];
  createdDateFilter?: string[];
  customDateRange?: { from: Date; to: Date } | null;
  businessUnitFilter?: string[];
  reactivationStatusFilter?: string[];
}

const INITIAL_BATCH_SIZE = 1000;
const BATCH_SIZE = 1000;

export function useContacts(filterNoDeals = false, options: UseContactsOptions = {}) {
  const { 
    page = 1, 
    limit = 200, 
    searchQuery = '', 
    salesConsultantFilter = [], 
    sdrFilter = [], 
    verificationFilter = [], 
    enabled = true,
    isKanbanMode = false,
    sourceFilter = [],
    channelFilter = [],
    contactTypeFilter = [],
    createdByFilter = [],
    dealStageFilter = [],
    createdDateFilter = [],
    customDateRange = null,
    businessUnitFilter = [],
    reactivationStatusFilter = []
  } = options;
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [backgroundProgress, setBackgroundProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const { permissions, getScope } = useUserPermissions();
  const backgroundLoadingRef = useRef(false);
  const loadIdRef = useRef(0);

  const buildBaseQuery = useCallback((withCount = true) => {
    // Determine if we need to join deals based on filters
    const needsDealStageFilter = dealStageFilter.length > 0 || filterNoDeals;
    const needsDealsJoin = needsDealStageFilter;
    
    // DEBUG: Log query building parameters
    console.log('🔧 [buildBaseQuery] Building query with:', {
      dealStageFilter,
      filterNoDeals,
      needsDealsJoin,
      willJoinDeals: needsDealsJoin
    });
    
    // Sempre incluir deals para resolver Consultor/SDR a partir do deal mais recente
    // (contato deixou de ter dono próprio — atribuição vive apenas no deal)
    // Se há filtro ativo de Consultor/SDR, usar !inner para que o filtro
    // query.in('deals.sales_consultant', ...) realmente restrinja os contatos
    // retornados (caso contrário o embed é left join e retorna todos os contatos).
    const consultantUserIdsForJoin = salesConsultantFilter.filter(id => id !== 'unassigned' && id !== 'me');
    const sdrUserIdsForJoin = sdrFilter.filter(id => id !== 'unassigned' && id !== 'me');
    const needsDealOwnerInner = consultantUserIdsForJoin.length > 0 || sdrUserIdsForJoin.length > 0;
    const dealsJoinHint = needsDealOwnerInner ? 'deals!fk_deals_contact!inner' : 'deals!fk_deals_contact';

    const selectQuery = `
          *,
          deals:${dealsJoinHint}(
            id,
            stage,
            created_at,
            sales_consultant,
            sales_development,
            sales_consultant_user:app_users!fk_deals_sold_by(id, first_name, last_name, display_name),
            sales_development_user:app_users!deals_sales_development_fkey(id, first_name, last_name, display_name)
          )
        `;

    let query: any = (supabase
      .from('contacts') as any)
      .select(selectQuery, { count: withCount ? 'exact' : undefined });

    // Handle deal stage filter (array-based)
    // Note: actual filtering for specific stages is done client-side
    // Here we just set up the join conditions

    // Apply search filter
    if (searchQuery && searchQuery.trim()) {
      const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/);
      searchTerms.forEach(term => {
        query = query.or(
          `first_name.ilike.%${term}%,` +
          `last_name.ilike.%${term}%,` +
          `primary_email.ilike.%${term}%,` +
          `primary_phone.ilike.%${term}%,` +
          `country_of_birth.ilike.%${term}%`
        );
      });
    }

    // Filtros de Consultor/SDR agora atuam sobre os DEALS do contato.
    // Contato é "do consultor X" se existe deal vinculado onde sales_consultant = X.
    // O sentinela 'unassigned' aqui significa "contato sem deal nenhum" e é resolvido
    // no client-side após a query (PostgREST não suporta NOT EXISTS via REST).
    if (salesConsultantFilter.length > 0) {
      const userIds = salesConsultantFilter.filter(id => id !== 'unassigned' && id !== 'me');
      if (userIds.length > 0) {
        // Inner join via deals; postgrest filtra contatos cujo array de deals tenha match
        query = query.in('deals.sales_consultant', userIds);
      }
    }

    if (sdrFilter.length > 0) {
      const userIds = sdrFilter.filter(id => id !== 'unassigned' && id !== 'me');
      if (userIds.length > 0) {
        query = query.in('deals.sales_development', userIds);
      }
    }

    // Apply verification status filter (array-based, only when not in kanban mode)
    if (verificationFilter.length > 0 && !isKanbanMode) {
      const hasUnverified = verificationFilter.includes('unverified');
      const otherStatuses = verificationFilter.filter(s => s !== 'unverified');
      
      if (hasUnverified && otherStatuses.length > 0) {
        const statusConditions = otherStatuses.map(s => `verification_status.eq.${s}`).join(',');
        query = query.or(`verification_status.is.null,verification_status.eq.unverified,${statusConditions}`);
      } else if (hasUnverified) {
        query = query.or('verification_status.is.null,verification_status.eq.unverified');
      } else if (otherStatuses.length > 0) {
        query = query.in('verification_status', otherStatuses);
      }
    }

    // Apply source filter (array-based)
    if (sourceFilter.length > 0) {
      query = query.in('contacts_source', sourceFilter);
    }

    // Apply channel filter (array-based)
    if (channelFilter.length > 0) {
      query = query.in('contacts_channel', channelFilter);
    }

    // Apply contact type filter (array-based)
    if (contactTypeFilter.length > 0) {
      query = query.in('contact_type', contactTypeFilter);
    }

    // Apply business unit filter (array-based)
    if (businessUnitFilter.length > 0) {
      const hasUnassigned = businessUnitFilter.includes('unassigned');
      const units = businessUnitFilter.filter(u => u !== 'unassigned');
      
      if (hasUnassigned && units.length > 0) {
        query = query.or(
          `business_unit.is.null,business_unit.in.(${units.join(',')})`
        );
      } else if (hasUnassigned) {
        query = query.is('business_unit', null);
      } else if (units.length > 0) {
        query = query.in('business_unit', units);
      }
    }

    // Apply created by filter (array-based)
    if (createdByFilter.length > 0) {
      const hasUnassigned = createdByFilter.includes('unassigned');
      const userIds = createdByFilter.filter(id => id !== 'unassigned' && id !== 'me');
      
      if (hasUnassigned && userIds.length > 0) {
        query = query.or(
          `created_by.is.null,created_by.in.(${userIds.join(',')})`
        );
      } else if (hasUnassigned) {
        query = query.is('created_by', null);
      } else if (userIds.length > 0) {
        query = query.in('created_by', userIds);
      }
    }

    // Apply created date filter (array-based - use first selected date filter)
    if (createdDateFilter.length > 0) {
      const now = new Date();
      let startDate: Date | null = null;
      let endDate: Date | null = null;
      
      // Use the first filter in the array
      const dateFilter = createdDateFilter[0];
      switch (dateFilter) {
        case 'today':
          startDate = startOfDay(now);
          endDate = endOfDay(now);
          break;
        case 'yesterday':
          startDate = startOfDay(subDays(now, 1));
          endDate = endOfDay(subDays(now, 1));
          break;
        case 'this_week':
          startDate = startOfWeek(now, { weekStartsOn: 0 });
          endDate = endOfWeek(now, { weekStartsOn: 0 });
          break;
        case 'this_month':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'custom':
          if (customDateRange?.from) {
            startDate = startOfDay(customDateRange.from);
            endDate = endOfDay(customDateRange.to || customDateRange.from);
          }
          break;
      }
      
      if (startDate && endDate) {
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }
    }

    // Apply reactivation status filter (array-based)
    if (reactivationStatusFilter.length > 0) {
      query = query.in('reactivation_status', reactivationStatusFilter);
    }

    query = query.order('created_at', { ascending: false });

    // O filtro de permissão por scope agora é aplicado pelas RLS policies do banco
    // (que olham para deals do contato). Não precisamos duplicar aqui.
    void getScope; void permissions;

    return query;
  }, [filterNoDeals, searchQuery, salesConsultantFilter, sdrFilter, verificationFilter, isKanbanMode, permissions, getScope, sourceFilter, channelFilter, contactTypeFilter, createdByFilter, dealStageFilter, createdDateFilter, customDateRange, businessUnitFilter, reactivationStatusFilter]);

  // Client-side filtering for deal stage (PostgREST doesn't filter parent rows on LEFT JOIN)
  const filterContactsByDealStage = useCallback((contactList: Contact[], dealFilter: string[]): Contact[] => {
    // DEBUG: Log filter parameters and sample data
    const sampleWithDeals = contactList.find(c => c.deals && Array.isArray(c.deals) && c.deals.length > 0);
    const sampleWithoutDeals = contactList.find(c => !c.deals || !Array.isArray(c.deals) || c.deals.length === 0);
    console.log('🔍 [filterContactsByDealStage] Input:', {
      dealFilter,
      totalContacts: contactList.length,
      sampleWithDeals: sampleWithDeals ? { id: sampleWithDeals.id, deals: sampleWithDeals.deals } : 'NONE FOUND',
      sampleWithoutDeals: sampleWithoutDeals ? { id: sampleWithoutDeals.id, deals: sampleWithoutDeals.deals } : 'NONE FOUND',
      contactsWithDealsCount: contactList.filter(c => c.deals && Array.isArray(c.deals) && c.deals.length > 0).length,
      contactsWithoutDealsCount: contactList.filter(c => !c.deals || !Array.isArray(c.deals) || c.deals.length === 0).length
    });
    
    if (dealFilter.length === 0) return contactList;
    
    const hasNoDeals = dealFilter.includes('no_deals');
    const hasAnyDeal = dealFilter.includes('any_deal');
    const specificStages = dealFilter.filter(f => f !== 'no_deals' && f !== 'any_deal');
    
    return contactList.filter(c => {
      const hasDeals = c.deals && Array.isArray(c.deals) && c.deals.length > 0;
      
      // Check if contact matches any of the selected filters
      if (hasNoDeals && !hasDeals) return true;
      if (hasAnyDeal && hasDeals) return true;
      if (specificStages.length > 0 && hasDeals) {
        return c.deals.some(deal => specificStages.includes(deal.stage));
      }
      
      return false;
    });
  }, []);

  const loadRemainingContactsInBackground = useCallback(async (
    initialContacts: Contact[],
    total: number,
    loadId: number
  ) => {
    if (backgroundLoadingRef.current) return;
    backgroundLoadingRef.current = true;
    setLoadingMore(true);
    
    let allContacts = [...initialContacts];
    const remainingCount = total - INITIAL_BATCH_SIZE;
    const batches = Math.ceil(remainingCount / BATCH_SIZE);
    
    console.log(`📦 Starting background load: ${remainingCount} remaining contacts in ${batches} batches`);
    
    for (let batch = 0; batch < batches; batch++) {
      // Check if this load was cancelled (new load started)
      if (loadIdRef.current !== loadId) {
        console.log('⚠️ Background loading cancelled - new load started');
        backgroundLoadingRef.current = false;
        setLoadingMore(false);
        return;
      }
      
      const from = INITIAL_BATCH_SIZE + (batch * BATCH_SIZE);
      const to = from + BATCH_SIZE - 1;
      
      try {
        const query = buildBaseQuery(false).range(from, to);
        const { data, error: err } = await query;
        
        if (err) {
          console.error('Background batch error:', err);
          continue;
        }
        
        // Check again after async operation
        if (loadIdRef.current !== loadId) {
          console.log('⚠️ Background loading cancelled after fetch');
          backgroundLoadingRef.current = false;
          setLoadingMore(false);
          return;
        }
        
        if (data && data.length > 0) {
          let batchData = (data as unknown) as Contact[];
          // Apply client-side deal stage filtering
          batchData = filterContactsByDealStage(batchData, dealStageFilter);
          allContacts = [...allContacts, ...batchData];
          setContacts(allContacts);
          
          const progress = Math.round(((batch + 1) / batches) * 100);
          setBackgroundProgress(progress);
          console.log(`📦 Batch ${batch + 1}/${batches} loaded: ${allContacts.length} filtered contacts`);
        }
      } catch (err) {
        console.error('Background batch exception:', err);
      }
    }
    
    setLoadingMore(false);
    setBackgroundProgress(100);
    backgroundLoadingRef.current = false;
    console.log(`✅ Background load complete: ${allContacts.length} filtered contacts loaded`);
  }, [buildBaseQuery, filterContactsByDealStage, dealStageFilter]);

  useEffect(() => {
    if (enabled === false) return;
    loadContacts();
  }, [filterNoDeals, permissions, page, limit, searchQuery, salesConsultantFilter, sdrFilter, verificationFilter, enabled, isKanbanMode, sourceFilter, channelFilter, contactTypeFilter, createdByFilter, dealStageFilter, createdDateFilter, customDateRange, businessUnitFilter, reactivationStatusFilter]);

  async function loadContacts(append = false) {
    // Cancel any previous background loading
    const currentLoadId = ++loadIdRef.current;
    backgroundLoadingRef.current = false;
    
    try {
      setLoading(true);
      setBackgroundProgress(0);
      
      // CRITICAL: Clear contacts immediately if not appending
      // This prevents showing stale data from previous mode/query
      if (!append) {
        setContacts([]);
      }
      
      if (isKanbanMode) {
        // KANBAN MODE: Hybrid loading
        const query = buildBaseQuery(true).range(0, INITIAL_BATCH_SIZE - 1);
        const { data, error: err, count } = await query;

        if (err) throw err;
        
        // Check if this load was cancelled
        if (loadIdRef.current !== currentLoadId) return;
        
        let contactData = ((data || []) as unknown) as Contact[];
        // Apply client-side deal stage filtering
        contactData = filterContactsByDealStage(contactData, dealStageFilter);
        setContacts(contactData);
        setTotalCount(count || 0);
        setHasMore(false);
        
        console.log(`🎯 Kanban initial load: ${contactData.length} filtered contacts (from ${data?.length || 0} raw), total in DB: ${count}`);
        
        // Start background loading if there's more
        if (count && count > INITIAL_BATCH_SIZE) {
          loadRemainingContactsInBackground(contactData, count, currentLoadId);
        }
      } else {
        // NORMAL MODE: Paginated loading
        const from = append ? contacts.length : (page - 1) * limit;
        const to = from + limit - 1;
        
        const query = buildBaseQuery(true).range(from, to);
        const { data, error: err, count } = await query;

        if (err) throw err;
        
        // Check if this load was cancelled
        if (loadIdRef.current !== currentLoadId) return;
        
        let contactData = ((data || []) as unknown) as Contact[];
        // Apply client-side deal stage filtering
        contactData = filterContactsByDealStage(contactData, dealStageFilter);
        
        if (append) {
          setContacts(prev => [...prev, ...contactData]);
        } else {
          setContacts(contactData);
        }
        
        setTotalCount(count || 0);
        setHasMore(contactData.length === limit);
      }
    } catch (err) {
      if (loadIdRef.current === currentLoadId) {
        setError(err as Error);
      }
    } finally {
      if (loadIdRef.current === currentLoadId) {
        setLoading(false);
      }
    }
  }

  async function loadMore() {
    await loadContacts(true);
  }

  async function createContact(contact: Partial<Contact>) {
    setIsCreating(true);
    try {
      // Check for duplicate phone before inserting
      if (contact.primary_phone && contact.primary_phone.trim() !== '') {
        const { data: existingContact } = await supabase
          .rpc('check_duplicate_contact_phone' as any, {
            p_phone: contact.primary_phone,
            p_country_code: contact.primary_phone_country_code || null,
            p_exclude_id: null
          })
          .maybeSingle();

        if (existingContact) {
          const existing = existingContact as any;
          const name = existing.first_name && existing.last_name
            ? `${existing.first_name} ${existing.last_name}`
            : existing.first_name || existing.last_name || 'Desconhecido';
          throw new Error(`Telefone ${contact.primary_phone} já está cadastrado para o contato ${name}`);
        }
      }

      const { data, error: err } = await supabase
        .from('contacts')
        .insert([contact])
        .select()
        .maybeSingle();

      if (err) {
        // Handle database unique violation error
        if (err.code === '23505' || err.message.includes('já está cadastrado')) {
          const match = err.message.match(/Telefone (.+) já está cadastrado para o contato (.+)/);
          if (match) {
            throw new Error(`Telefone ${match[1]} já cadastrado para ${match[2]}`);
          }
          throw new Error('Este telefone já está cadastrado para outro contato');
        }
        throw err;
      }
      
      if (enabled !== false) {
        await loadContacts();
      }
      return data as Contact;
    } finally {
      setIsCreating(false);
    }
  }

  async function updateContact(id: string, updates: Partial<Contact>) {
    // Check for duplicate phone before updating (if phone is being changed)
    if (updates.primary_phone && updates.primary_phone.trim() !== '') {
      const { data: existingContact } = await supabase
        .rpc('check_duplicate_contact_phone' as any, {
          p_phone: updates.primary_phone,
          p_country_code: updates.primary_phone_country_code || null,
          p_exclude_id: id
        })
        .maybeSingle();

      if (existingContact) {
        const existing = existingContact as any;
        const name = existing.first_name && existing.last_name
          ? `${existing.first_name} ${existing.last_name}`
          : existing.first_name || existing.last_name || 'Desconhecido';
        
        const duplicateError = new Error(
          `DUPLICATE_PHONE::${existing.id}::Telefone ${updates.primary_phone} já cadastrado para ${name}. ` +
          `Esses contatos precisam ser mesclados. Acesse Configurações > Mesclar Contatos para resolver.`
        );
        throw duplicateError;
      }
    }

    const { data, error: err } = await supabase
      .from('contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (err) {
      // Handle database unique violation error from trigger
      if (err.code === '23505' || err.message.includes('Duplicate phone number')) {
        // Extract contact name from trigger error message
        const match = err.message.match(/already exists for contact (.+)/);
        const contactName = match ? match[1] : 'outro contato';
        
        throw new Error(
          `DUPLICATE_DETECTED::Telefone ${updates.primary_phone || ''} já cadastrado para ${contactName}. ` +
          `Esses contatos precisam ser mesclados. Acesse Configurações > Mesclar Contatos.`
        );
      }
      throw err;
    }
    
    await loadContacts();
    return data;
  }

  async function deleteContact(id: string) {
    const { error: err } = await supabase
      .from('contacts')
      .delete()
      .eq('id', id);

    if (err) throw err;
    await loadContacts();
  }

  return {
    contacts,
    totalCount,
    loading,
    loadingMore,
    backgroundProgress,
    error,
    hasMore,
    loadContacts,
    loadMore,
    createContact,
    updateContact,
    deleteContact,
    isCreating,
  };
}

export function useContact(id: string) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    loadContact();
  }, [id]);

  async function loadContact() {
    try {
      setLoading(true);
      const { data, error: err } = await (supabase
        .from('contacts') as any)
        .select(`
          *,
          deals:deals!fk_deals_contact(
            id,
            stage,
            created_at,
            sales_consultant,
            sales_development,
            sales_consultant_user:app_users!fk_deals_sold_by(id, first_name, last_name, display_name),
            sales_development_user:app_users!deals_sales_development_fkey(id, first_name, last_name, display_name)
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (err) throw err;
      setContact(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }

  return { contact, loading, error, loadContact };
}
